import { PackageOpen } from "lucide-react";

import type { StockItem } from "@/hooks/queries/inventory/useStockItems";
import type { ProcedureConsumableInput } from "@/types";

type ProcedureConsumablesFieldsProps = {
  items: StockItem[];
  value: ProcedureConsumableInput[];
  onChange: (value: ProcedureConsumableInput[]) => void;
  isLoading?: boolean;
};

export function ProcedureConsumablesFields({
  items,
  value,
  onChange,
  isLoading = false,
}: ProcedureConsumablesFieldsProps) {
  const selected = new Map(value.map((item) => [item.stock_item_id, item]));

  function toggleItem(itemId: string, checked: boolean) {
    if (checked) {
      onChange([...value, { stock_item_id: itemId, quantity: 1 }]);
      return;
    }

    onChange(value.filter((item) => item.stock_item_id !== itemId));
  }

  function changeQuantity(itemId: string, quantity: number) {
    onChange(
      value.map((item) =>
        item.stock_item_id === itemId ? { ...item, quantity } : item,
      ),
    );
  }

  return (
    <section className="space-y-3 rounded-xl border border-zinc-200 bg-zinc-50/60 p-4">
      <div>
        <h3 className="text-sm font-semibold text-zinc-900">
          Insumos consumidos por atendimento
        </h3>
        <p className="mt-1 text-xs leading-relaxed text-zinc-500">
          Ao finalizar este procedimento, o estoque baixa automaticamente nas
          quantidades configuradas.
        </p>
      </div>

      {isLoading ? (
        <p className="text-sm text-zinc-500">Carregando insumos...</p>
      ) : items.length === 0 ? (
        <div className="flex items-center gap-2 rounded-lg border border-dashed border-zinc-200 bg-white p-3 text-sm text-zinc-500">
          <PackageOpen className="size-4" />
          Cadastre itens no Estoque antes de configurar o consumo.
        </div>
      ) : (
        <div className="max-h-56 space-y-2 overflow-y-auto pr-1">
          {items.map((item) => {
            const consumable = selected.get(item.id);
            const isSelected = Boolean(consumable);

            return (
              <div
                key={item.id}
                className="grid grid-cols-[1fr_auto] items-center gap-3 rounded-lg border border-zinc-200 bg-white p-3"
              >
                <label className="flex min-w-0 cursor-pointer items-center gap-3">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={(event) =>
                      toggleItem(item.id, event.target.checked)
                    }
                    className="size-4 rounded border-zinc-300 accent-teal-600"
                  />
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-medium text-zinc-800">
                      {item.name}
                    </span>
                    <span className="block text-xs text-zinc-500">
                      Saldo: {item.current_quantity} {item.unit}
                    </span>
                  </span>
                </label>

                {isSelected && (
                  <label className="flex items-center gap-2 text-xs font-medium text-zinc-600">
                    <span className="sr-only">Consumo de {item.name}</span>
                    <input
                      aria-label={`Consumo de ${item.name}`}
                      type="number"
                      min="0.001"
                      step="0.001"
                      value={consumable?.quantity ?? 1}
                      onChange={(event) =>
                        changeQuantity(item.id, Number(event.target.value))
                      }
                      className="h-9 w-24 rounded-md border border-zinc-200 bg-white px-2 text-right text-sm outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-500/15"
                    />
                    <span>{item.unit}</span>
                  </label>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
